import { Injectable } from "@angular/core";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { TranslateService } from "@ngx-translate/core";
import { Big } from "big.js";
import { OrderModel } from "src/app/models/order.model";
import { IPayment } from "src/app/models/payment.model";
import { ProductModel } from "src/app/models/product.model";
import { TourneeModel } from "src/app/models/tournee.model";
import currencies from "../../../assets/data/currencies.json";
import VAT from "../../../assets/data/VAT.json";

export interface Totals {
  VATExcluded: number;
  VATIncluded: number;
}

@Injectable({
  providedIn: "root"
})
export class MathService {
  conversionTable = {
    gram: {
      "100gram": 0.01,
      kilogram: 0.001
    }
  };

  // Fabrique un objet finances NEUF à chaque appel. Ne jamais partager une
  // instance : les calculs mutent les Big imbriqués (finances.X = finances.X.plus(...)),
  // donc une instance réutilisée accumulerait les totaux d'une commande à l'autre.
  public getEmptyFinances() {
    return {
      orderCount: 0,
      orderRejected: 0,
      productCount: 0,
      totals: {
        amountStatus: 'final',
        paid: new Big(0),
        sold: new Big(0),
        prepayable: new Big(0),
        soldWithOutShipping: new Big(0),
        shippingCost: new Big(0),
        calculated: new Big(0),
        commission: new Big(0),
        products: new Big(0),
        deposit: new Big(0)
      },
      VATExcluded: {
        amountStatus: 'final',
        paid: new Big(0),
        sold: new Big(0),
        prepayable: new Big(0),
        soldWithOutShipping: new Big(0),
        shippingCost: new Big(0),
        calculated: new Big(0),
        commission: new Big(0),
        products: new Big(0),
        deposit: new Big(0)
      },
      VATIncluded: {
        amountStatus: 'final',
        paid: new Big(0),
        prepayable: new Big(0),
        sold: new Big(0),
        soldWithOutShipping: new Big(0),
        shippingCost: new Big(0),
        calculated: new Big(0),
        commission: new Big(0),
        products: new Big(0),
        deposit: new Big(0)
      },
      subtypes: {}
    };
  }

  constructor(private translateService: TranslateService) {}

  public sumProducts(products: ProductModel[]) {
    const reducer = (accumulator, currentValue): ProductModel[] => {
      return new Big(accumulator).plus(new Big(this.calculateProductTotalAmount(currentValue))).toFixed(2);
    };
    return products.reduce(reducer, 0);
  }

  public calculateDeposit(product: ProductModel) {
    const quantityBig = product.details.quantityMetricType === "weight" ? Big(1) : Big(product.basket.quantity);
    const depositBig = new Big(product.details.deposit || 0);
    const resultBig = depositBig.times(quantityBig).round(2);
    // console.log(product.details , resultBig.toFixed(2));
    return this.bigToFloat(resultBig);
  }

  public calculateProductTotalAmount(product: ProductModel): number {
    let conversion = 1;
    let size = 1;
    let defaultMetric = "gram";
    let quantity = 0;
    if (product.basket && product.basket.volumeDelivered !== null && typeof product.basket.volumeDelivered !== "undefined") {
      quantity = product.basket.volumeDelivered;
      conversion = this.conversionTable[product.basket.volumeDeliveredMetric || defaultMetric][product.details.unitPriceQuantityMetric];
    } else if (product.basket) {
      quantity =
        product.basket.quantityDelivered !== null && typeof product.basket.quantityDelivered !== "undefined"
          ? product.basket.quantityDelivered
          : product.basket.quantity || 0;
      if (product.details.unitPriceQuantityMetricType == "weight") {
        if (product.details.quantityMetricType == "weight") {
          conversion = this.conversionTable[product.details.quantityMetric][product.details.unitPriceQuantityMetric];
        } else if (product.details.quantityMetricType == "piece") {
          size = product.details.sizeDefault;
          conversion = this.conversionTable[product.details.sizeMetric][product.details.unitPriceQuantityMetric];
        }
      } else if (product.details.quantityMetricType == "none") {
        quantity = 1;
        product.basket.unitPriceVATIncluded = product.basket.totalPriceVATIncluded;
      }
    }
    const quantityBig = new Big(quantity);
    const price =
      product.basket.unitPriceVATIncluded !== null && typeof product.basket.unitPriceVATIncluded !== "undefined"
        ? product.basket.unitPriceVATIncluded
        : product.details.unitPriceVATIncluded;
    const priceBig = new Big(price);
    const conversionBig = new Big(conversion);
    const sizeBig = new Big(size || 1);
    let resultBig = new Big(0);

    resultBig = conversionBig.times(quantityBig).times(priceBig).times(sizeBig).round(2);
    return parseFloat(resultBig.toFixed(2));
  }

  public calculateProductVATExcludedPrice(product: ProductModel, countryCode: string) {
    const price = product.basket?.unitPriceVATIncluded || product.details.unitPriceVATIncluded || 0;
    // Garde : VAT[countryCode] peut être undefined (pays absent de VAT.json ou
    // countryCode en 2 lettres) -> l'accès [type] planterait.
    const VATRate = product.details.VATRate || (VAT[countryCode] && VAT[countryCode][product.details.type]) || 0;
    return this.calculateVATExcludedPrice(price, VATRate, product.details.currency);
  }

  amountToProduct(amount: number, type: string, currency: string): ProductModel {
    return {
      details: {
        type: type,
        currency: currency,
        unitPriceVATIncluded: amount
      }
    };
  }

  calculateSold(finances: any) {
    return finances.products.plus(finances.deposit).plus(finances.shippingCost).plus(finances.commission);
  }

  public calculateProductTotalsAmount(product: ProductModel, countryCode?: string): Totals {
    let conversion = 1;
    let size = 1;
    let defaultMetric = "gram";
    let quantity = 0;
    if (!product.basket.unitPriceVATExcluded) {
      product.basket.unitPriceVATExcluded = this.calculateProductVATExcludedPrice(product, countryCode || "FRA");
    }
    if (product.basket && product.basket.volumeDelivered !== null && typeof product.basket.volumeDelivered !== "undefined") {
      quantity = product.basket.volumeDelivered;
      conversion = this.conversionTable[product.basket.volumeDeliveredMetric || defaultMetric][product.details.unitPriceQuantityMetric];
    } else if (product.basket) {
      quantity =
        product.basket.quantityDelivered !== null && typeof product.basket.quantityDelivered !== "undefined"
          ? product.basket.quantityDelivered
          : product.basket.quantity || 0;
      if (product.details.unitPriceQuantityMetricType == "weight") {
        if (product.details.quantityMetricType == "weight") {
          conversion = this.conversionTable[product.details.quantityMetric][product.details.unitPriceQuantityMetric];
        } else if (product.details.quantityMetricType == "piece") {
          size = product.details.sizeDefault;
          conversion = this.conversionTable[product.details.sizeMetric][product.details.unitPriceQuantityMetric];
        }
      } else if (product.details.quantityMetricType == "none") {
        quantity = 1;
        product.basket.unitPriceVATIncluded = product.basket.totalPriceVATIncluded;
      }
    }
    const quantityBig = new Big(quantity);
    const priceVAT =
      product.basket.unitPriceVATIncluded !== null && typeof product.basket.unitPriceVATIncluded !== "undefined"
        ? product.basket.unitPriceVATIncluded
        : product.details.unitPriceVATIncluded;
    const priceVATBig = new Big(priceVAT);
    const priceBig = new Big(product.basket.unitPriceVATExcluded);
    const conversionBig = new Big(conversion);
    const sizeBig = new Big(size || 1);
    let resultVATBig = new Big(0);
    let resultBig = new Big(0);

    resultVATBig = conversionBig.times(quantityBig).times(priceVATBig).times(sizeBig).round(2);
    resultBig = conversionBig.times(quantityBig).times(priceBig).times(sizeBig).round(2);
    return {
      VATIncluded: parseFloat(resultVATBig.toFixed(2)),
      VATExcluded: parseFloat(resultBig.toFixed(2))
    } as Totals;
  }
  public getConversion(product: ProductModel) {
    const conversionTable = {
      gram: {
        "100gram": 0.01,
        kilogram: 0.001
      }
    };
    const defaultMetric = "gram";
    let conversion = 1;
    if (product.basket && product.basket.volumeDelivered !== null && typeof product.basket.volumeDelivered !== "undefined") {
      conversion = this.conversionTable[product.basket.volumeDeliveredMetric || defaultMetric][product.details.unitPriceQuantityMetric];
    } else if (product.basket) {
      if (product.details.unitPriceQuantityMetricType == "weight") {
        if (product.details.quantityMetricType == "weight") {
          conversion = this.conversionTable[product.details.quantityMetric][product.details.unitPriceQuantityMetric];
        } else if (product.details.quantityMetricType == "piece") {
          conversion = this.conversionTable[product.details.sizeMetric][product.details.unitPriceQuantityMetric];
        }
      }
    }
    return conversion;
  }

  public calculateProductPrice(product: ProductModel): number {
    let conversion = 1;
    let size = 1;
    let defaultMetric = "gram";
    let quantity = 0;
    if (product.basket && product.basket.volumeDelivered !== null && typeof product.basket.volumeDelivered !== "undefined") {
      quantity = product.basket.volumeDelivered;
      conversion = this.conversionTable[product.basket.volumeDeliveredMetric || defaultMetric][product.details.unitPriceQuantityMetric];
    } else if (product.basket) {
      quantity =
        product.basket.quantityDelivered !== null && typeof product.basket.quantityDelivered !== "undefined"
          ? product.basket.quantityDelivered
          : product.basket.quantity || 0;
      if (product.details.unitPriceQuantityMetricType == "weight") {
        if (product.details.quantityMetricType == "weight") {
          conversion = this.conversionTable[product.details.quantityMetric][product.details.unitPriceQuantityMetric];
        } else if (product.details.quantityMetricType == "piece") {
          size = product.details.sizeDefault;
          conversion = this.conversionTable[product.details.sizeMetric][product.details.unitPriceQuantityMetric];
        }
      } else if (product.details.quantityMetricType == "none") {
        quantity = 1;
        product.basket.unitPriceVATIncluded = product.basket.totalPriceVATIncluded;
      }
    }
    const quantityBig = new Big(quantity);
    const price = product.basket.totalPriceVATIncluded;
    const priceBig = new Big(price);
    const conversionBig = new Big(conversion);
    const sizeBig = new Big(size);
    let resultBig = new Big(0);

    resultBig = priceBig.div(conversionBig.times(quantityBig).times(sizeBig).round(2));
    return parseFloat(resultBig.toFixed(2));
  }

  public convertGrToKgStr(quantity) {
    let quantityBig = new Big(quantity);
    quantityBig = quantityBig.div(1000);
    return quantityBig.toFixed(2);
  }

  public convertQuantity(quantity, metric: string) {
    if (metric == "gram" && quantity >= 1000) {
      return this.convertGrToKgStr(quantity);
    } else {
      return quantity;
    }
  }

  public getRandomInt(min, max) {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
  }

  public multiply(a, b) {
    const aBig = new Big(a);
    const bBig = new Big(b);
    return parseFloat(aBig.times(bBig).toFixed(2));
  }

  public substract(a, b) {
    const aBig = new Big(a);
    const bBig = new Big(b);
    return parseFloat(aBig.minus(bBig).toFixed(2));
  }

  public getTourneeCountryCode(tournee: TourneeModel) {
    return tournee.producer?.details?.geocoding.address.countryCode 
      || tournee.owner?.details?.geocoding.address.countryCode 
      || tournee.details.datesPlaces[0].geocoding.address.countryCode 
      || tournee.details.geocoding.address.countryCode || "FRA";
  }

  public getOrderCountryCode(order: OrderModel) {
    return this.getTourneeCountryCode(order.tournee || order.market);
  }


  public calculateMultiOrder(order: OrderModel) {
    // console.log('calculateMultiOrder', order);
    // USED ONLY FOR NEW ORDER in market or tournee
    const finances = this.getEmptyFinances();
    if (order.products.references["deliveryCost"]) {
      delete order.products.references["deliveryCost"];
    }
    if (order.products.references["deposit"]) {
      delete order.products.references["deposit"];
    }
    if (order.products.references["commission"]) {
      delete order.products.references["commission"];
    }
    let deliveryCost = order.market ? order.market.details.deliveryCost : order.tournee.details.deliveryCost;

    const commissionType = order.market ? order.market.details.commissionType : order.tournee.details.commissionType;
    const commissionValue = order.market ? order.market.details.commissionValue : order.tournee.details.commissionValue;
    const countryCode = this.getTourneeCountryCode(order.market || order.tournee);

    // MULTIPLE TOURNEES
    if (order.details.format == "group") {

      for (const [tourneeKey, value] of Object.entries(order.tournees)) {
        const tournee: TourneeModel = value;
        const tourneeFinances = this.getEmptyFinances();
        if (!!tournee.products && !!tournee.products.references) {
          for (const [productKey, value] of Object.entries(tournee.products.references)) {
            if (productKey !== "deposit" && productKey !== "deliveryCost" && productKey !== "commission") {
              const product: ProductModel = value;
              const sold = Big(this.calculateProductTotalAmount(product));
              const { VATIncluded, VATExcluded } = this.calculateProductTotalsAmount(product);

              // product deposit
              const deposit = this.calculateDeposit(product);
              const depositProduct = this.amountToProduct(deposit, "deposit", product.details.currency);
              const depositVATExcluded = this.calculateProductVATExcludedPrice(depositProduct, countryCode);
              tourneeFinances.totals.deposit = tourneeFinances.totals.deposit.plus(Big(deposit));
              tourneeFinances.VATExcluded.deposit = tourneeFinances.VATExcluded.deposit.plus(Big(deposit));
              tourneeFinances.VATIncluded.deposit = tourneeFinances.VATIncluded.deposit.plus(Big(depositVATExcluded));

              if (product.details.unitPriceQuantityMetricType == "weight") {
                order.tournees[tourneeKey].details.priceCalculationStatus = "prepared";
                order.details.priceCalculationStatus = "prepared";
                finances.totals.amountStatus = "estimated";
                tourneeFinances.totals.amountStatus = "estimated";
                order.tournees[tourneeKey].products.references[productKey].basket.amountStatus = "estimated";
                // if(order.tournees[tourneeKey].producer.details.paymentModes.mangopay == 'activated'){
                // }
              } else {
                // if(order.tournees[tourneeKey].producer.details.paymentModes.mangopay == 'activated'){
                // }
                order.tournees[tourneeKey].products.references[productKey].basket.amountStatus = "final";
              }
              tourneeFinances.totals.products = tourneeFinances.totals.products.plus(sold);
              tourneeFinances.VATExcluded.products = tourneeFinances.VATExcluded.products.plus(Big(VATExcluded));
              tourneeFinances.VATIncluded.products = tourneeFinances.VATIncluded.products.plus(Big(VATIncluded));

              order.tournees[tourneeKey].products.references[productKey].basket.totalPriceVATIncluded = VATIncluded;
              order.tournees[tourneeKey].products.references[productKey].basket.totalPriceVATExcluded = VATExcluded;
              tourneeFinances.productCount++;
            }
          }
        
            // tournee deposit
          const tourneeDepositProduct = this.amountToProduct(tournee.details.deposit, "deposit", tournee.details.currency);
          const tourneeDepositVATExcluded = this.calculateProductVATExcludedPrice(tourneeDepositProduct, countryCode);
          tourneeFinances.totals.deposit = tourneeFinances.totals.deposit.plus(Big(tournee.details.deposit || 0));
          tourneeFinances.VATExcluded.deposit = tourneeFinances.VATExcluded.deposit.plus(Big(tournee.details.deposit || 0));
          tourneeFinances.VATIncluded.deposit = tourneeFinances.VATIncluded.deposit.plus(Big(tourneeDepositVATExcluded || 0));
          // if total deposit (sum of sale and product deposits) > 0, we add/overwrite a deposit product
          if (this.bigToFloat(tourneeFinances.totals.deposit) > 0) {
            const depositProduct: ProductModel = {
              details: {
                unitPriceVATIncluded: this.bigToFloat(tourneeFinances.totals.deposit),
                name: this.translateService.instant("productCatalog.Deposit"),
                photoURL: "/assets/icon/deposit.svg",
                unitPriceQuantityMetric: "deposit",
                quantityMetric: "none",
                unitPriceQuantityMetricType: "none",
                quantityMetricType: "none",
                currency: tournee.details.currency,
                type: "deposit"
              },
              basket: {
                quantity: 1,
                totalPriceVATExcluded: this.bigToFloat(tourneeFinances.VATExcluded.deposit),
                totalPriceVATIncluded: this.bigToFloat(tourneeFinances.VATIncluded.deposit)
              }
            };
            order.tournees[tourneeKey].products.references["deposit"] = depositProduct;
          }

          finances.totals.products = finances.totals.products.plus(tourneeFinances.totals.products);
          finances.VATExcluded.products = finances.VATExcluded.products.plus(tourneeFinances.VATExcluded.products);
          finances.VATIncluded.products = finances.VATIncluded.products.plus(tourneeFinances.VATIncluded.products);

          if(commissionType === 'percentage'){
            tourneeFinances.VATExcluded.commission = this.calculateCommission(this.bigToFloat(tourneeFinances.VATExcluded.products),commissionType,commissionValue);
            tourneeFinances.VATIncluded.commission = this.calculateCommission(this.bigToFloat(tourneeFinances.VATIncluded.products),commissionType,commissionValue);
            tourneeFinances.totals.commission = this.calculateCommission(this.bigToFloat(tourneeFinances.totals.products),commissionType,commissionValue);          
          
            finances.VATExcluded.commission = finances.VATExcluded.commission.plus(tourneeFinances.VATExcluded.commission);
            finances.VATIncluded.commission = finances.VATIncluded.commission.plus(tourneeFinances.VATIncluded.commission);
            finances.totals.commission = finances.totals.commission.plus(tourneeFinances.totals.commission);
          }

          finances.totals.deposit = finances.totals.deposit.plus(tourneeFinances.totals.deposit);
          finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(tourneeFinances.VATExcluded.deposit);
          finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(tourneeFinances.VATIncluded.deposit);
          
          finances.productCount += tourneeFinances.productCount;
        }

        // the total of products and the deposit are added to the total sold (total amount due by the client) for the Tournee
        tourneeFinances.totals.sold = this.calculateSold(tourneeFinances.totals);
        tourneeFinances.VATExcluded.sold = this.calculateSold(tourneeFinances.VATExcluded);
        tourneeFinances.VATIncluded.sold = this.calculateSold(tourneeFinances.VATIncluded);

        finances.orderCount++;
        order.tournees[tourneeKey].finances = this.financesBigToFloat(tourneeFinances);
        // console.log("calculate multi", order.tournees[tourneeKey].finances);
      }
      if(commissionType === 'fixed'){
        for (const value of Object.values(order.tournees)) {
          const tournee: TourneeModel = value;
          const fixedCommissionVATIncluded = commissionValue * tournee.finances.totals.products / finances.totals.products;
          const fixedCommissionProduct = this.amountToProduct(fixedCommissionVATIncluded, "commission", tournee.details.currency);
          const fixedCommissionVATExcluded = this.calculateProductVATExcludedPrice(fixedCommissionProduct, countryCode);
          tournee.finances.VATExcluded.commission = fixedCommissionVATExcluded;
          tournee.finances.VATIncluded.commission = fixedCommissionVATIncluded;
          tournee.finances.totals.commission = fixedCommissionVATIncluded;
          
          finances.VATExcluded.commission = finances.VATExcluded.commission.plus(Big(fixedCommissionVATExcluded));
          finances.VATIncluded.commission = finances.VATIncluded.commission.plus(Big(fixedCommissionVATIncluded));
          finances.totals.commission = finances.totals.commission.plus(Big(fixedCommissionVATIncluded));
        }
      }
    }
    // SINGLE TOURNEE
    else {
      if (order.products && !!order.products.references) {
        for (const [productKey, value] of Object.entries(order.products.references)) {
          if (productKey !== "deposit" && productKey !== "deliveryCost" && productKey !== "commission") {
            const product: ProductModel = value;
            const sold = Big(this.calculateProductTotalAmount(product));
            const { VATIncluded, VATExcluded } = this.calculateProductTotalsAmount(product);
            if (product.details.unitPriceQuantityMetricType == "weight") {
              order.details.priceCalculationStatus = "prepared";
              finances.totals.amountStatus = "estimated";
              // if(order.tournee.producer.details.paymentModes.mangopay == 'activated'){
              order.products.references[productKey].basket.amountStatus = "estimated";
              // }
            } else {
              // if(order.tournee.producer.details.paymentModes.mangopay == 'activated'){
              // }
            }
            order.products.references[productKey].basket.totalPriceVATIncluded = parseFloat(sold.toFixed(2));
            finances.totals.products = finances.totals.products.plus(sold);
            finances.VATExcluded.products = finances.VATExcluded.products.plus(Big(VATExcluded));
            finances.VATIncluded.products = finances.VATIncluded.products.plus(Big(VATIncluded));
            
            const deposit = this.calculateDeposit(product);
            const depositProduct = this.amountToProduct(deposit, "deposit", product.details.currency);
            const depositVATExcluded = this.calculateProductVATExcludedPrice(depositProduct, countryCode);
            finances.totals.deposit = finances.totals.deposit.plus(Big(deposit));
            finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(Big(deposit));
            finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(Big(depositVATExcluded));
            
            finances.productCount++;
            // console.log(finances.totals.deposit.toFixed(2), depositBig.toFixed(2));
          }
        }
        const tourneeDepositProduct = this.amountToProduct(order.tournee.details.deposit, "deposit", order.tournee.details.currency);
        const tourneeDepositVATExcluded = this.calculateProductVATExcludedPrice(tourneeDepositProduct, countryCode);
        finances.totals.deposit = finances.totals.deposit.plus(Big(order.tournee.details.deposit || 0));
        finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(Big(order.tournee.details.deposit || 0));
        finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(Big(tourneeDepositVATExcluded || 0));

        if (this.bigToFloat(finances.totals.deposit) > 0) {
          const depositProduct: ProductModel = {
            details: {
              unitPriceVATIncluded: this.bigToFloat(finances.totals.deposit),
              name: this.translateService.instant("productCatalog.Deposit"),
              photoURL: "/assets/icon/deposit.svg",
              unitPriceQuantityMetric: "deposit",
              quantityMetric: "none",
              unitPriceQuantityMetricType: "none",
              quantityMetricType: "none",
              currency: order.tournee.details.currency,
              type: "deposit"
            },
            basket: {
              quantity: 1,
              totalPriceVATExcluded: this.bigToFloat(finances.VATExcluded.deposit),
              totalPriceVATIncluded: this.bigToFloat(finances.VATIncluded.deposit)
            }
          };
          order.products.references["deposit"] = depositProduct;
        }

        if (commissionValue){
          finances.VATExcluded.commission = this.calculateCommission(this.bigToFloat(finances.VATExcluded.products),commissionType,commissionValue);
          finances.VATIncluded.commission = this.calculateCommission(this.bigToFloat(finances.VATIncluded.products),commissionType,commissionValue);
          finances.totals.commission = this.calculateCommission(this.bigToFloat(finances.totals.products),commissionType,commissionValue);
        }
        finances.orderCount++;
      }
    }
    
    if (finances.productCount > 0 && deliveryCost && deliveryCost > 0) {
      // console.log('add delivery cost');
      const deliveryProduct: ProductModel = {
        details: {
          unitPriceVATIncluded: deliveryCost,
          name: this.translateService.instant("tournee.DeliveryFee"),
          photoURL: "/assets/photos/delivery.jpg",
          unitPriceQuantityMetric: "delivery",
          quantityMetric: "none",
          unitPriceQuantityMetricType: "none",
          quantityMetricType: "none",
          type: "delivery"
        },
        basket: {
          quantity: 1,
          totalPriceVATIncluded: deliveryCost,
          totalPriceVATExcluded: null
        }
      };
      const deliveryProductVATExcluded = this.calculateProductVATExcludedPrice(deliveryProduct, countryCode);
      finances.totals.shippingCost = finances.totals.shippingCost.plus(Big(deliveryCost));
      finances.VATExcluded.shippingCost = finances.VATExcluded.shippingCost.plus(Big(deliveryCost || 0));
      finances.VATIncluded.shippingCost = finances.VATIncluded.shippingCost.plus(Big(deliveryProductVATExcluded || 0));

      deliveryProduct.basket.totalPriceVATExcluded = deliveryProductVATExcluded;

      order.products.references["deliveryCost"] = deliveryProduct;
    }
    
    if (finances.productCount > 0 && finances.VATIncluded.commission > 0) {
      const commissionProduct: ProductModel = {
        details: {
          unitPriceVATIncluded: finances.VATIncluded.commission,
          name: this.translateService.instant("tournee.commission.CommissionPickup"),
          photoURL: "/assets/photos/delivery.jpg",
          unitPriceQuantityMetric: "commission",
          quantityMetric: "none",
          unitPriceQuantityMetricType: "none",
          quantityMetricType: "none",
          type: "commission"
        },
        basket: {
          quantity: 1,
          totalPriceVATIncluded: finances.VATIncluded.commission,
          totalPriceVATExcluded: finances.VATExcluded.commission
        }
      };
      order.products.references["commission"] = commissionProduct;
    }

    finances.totals.sold = this.calculateSold(finances.totals);
    finances.VATExcluded.sold = this.calculateSold(finances.VATExcluded);
    finances.VATIncluded.sold = this.calculateSold(finances.VATIncluded);
    finances.totals.paid =
      order.paymentStatus == "cashedIn" || order.paymentStatus == "cashed"
        ? finances.totals.paid.plus(finances.totals.sold)
        : finances.totals.paid;
    finances.totals.prepayable = finances.totals.prepayable.plus(finances.totals.sold);

    order.finances = this.financesBigToFloat(finances);
    // console.log(order);
    return order;
  }

  // public updateProductStock(oldProduct:any,newProduct:any, tournee:any, producer:any){
  //   if(oldProduct.stock){
  //       const currentSnap = await admin.database().ref('/producers/'+producer.key + '/tournees/'+ tournee.key + '/products/references/'+ oldProduct.key+'/stock/current').once("value");
  //       const oldValue = oldProduct.basket.quantityDelivered || oldProduct.basket.quantity;
  //       const newValue = newProduct.basket.quantityDelivered || newProduct.basket.quantity;
  //       const difference = newValue - oldValue;
  //       const current = Math.max(0,(currentSnap.val() - difference));
  //       await admin.database().ref('/producers/'+producer.key + '/tournees/'+ tournee.key + '/products/references/'+ oldProduct.key+'/stock/current').set(current);
  //       if(current <= 0){
  //           await admin.database().ref('/producers/'+producer.key + '/tournees/'+ tournee.key + '/products/references/'+ oldProduct.key+'/stockStatus').set('outOfStock');
  //       }
  //   }
  //   return;
  // }

  public calculateTourneeRevenue2(tournee: TourneeModel, computeRejected = false): TourneeModel {
    // console.log({...tournee});
    // USED on tournee manage to refresh the values when the producer change the payment status or set an amount
    let finances = this.getEmptyFinances();
    if (tournee.orders) {
      for (const [key, value] of Object.entries(tournee.orders)) {
        let order: OrderModel = value;

        if (!order.tournee || !order.tournee.details) {
          // console.log('add tournee');
          order.tournee = { details: tournee.details, key: tournee.key };
        }
        order = this.calculateOrderRevenue(order);
        tournee.orders[key] = order;
        finances = this.addFinances(finances, order.finances);
        if (order.payments) {
          for (const [key, value] of Object.entries(order.payments)) {
            const payment: IPayment = value;
            if (payment.status == "paid") {
              const paid = new Big(payment.debitedFunds.amount);
              finances.totals.paid = finances.totals.paid.plus(paid);
              finances.VATIncluded.paid = finances.VATIncluded.paid.plus(paid);
            }
          }
        }
      }
    }
    tournee.finances = this.financesBigToFloat(finances);
    tournee.paymentStatus = this.checkPaymentStatus(tournee.finances);
    return tournee;
  }

  // public formatPrice(finances:any, currency: string, type:string, priceMode: string, displayMode?: string ){
  //   let display = '';
  //   const status = finances.totals?.amountStatus;
  //   display += status=='estimated'?'±':''
  //   if(!displayMode || displayMode === 'priceOnly'){
  //     currency = currency.toUpperCase();
  //     const price = priceMode === 'VATExcluded' ? finances.VATExcluded[type] : finances?.VATIncluded[type];
  //     display += this.currencyPipe.transform(price,currency.toUpperCase());
  //   }
  //   if(!displayMode || displayMode === 'detailsOnly'){
  //     display += priceMode === 'VATExcluded' ? this.translateService.instant('tax.VATExcludedShort'): '';
  //   }
  //   return display;
  // }

  public calculateOrderRevenue(order: OrderModel) {
    // console.log('calculateOrderRevenue', order);
    // for single tournee order (used when recalculating order from producer view)
    const countryCode = this.getTourneeCountryCode(order.tournee);
    const currency = order.details.currency;
    if (order.products && order.products.references && Object.entries(order.products.references).length > 0) {
      const finances = this.getEmptyFinances();
      (finances.orderCount = 1),
        (finances.orderRejected = ["notDelivered", "cancelled", "rejected"].includes(order.status) ? 1 : 0),
        (finances.totals.sold = new Big(
          order.finances && order.finances.totals && order.finances.totals.sold 
          && order.finances.totals.amountStatus == "setByProducer"
            ? order.finances.totals.sold
            : 0
        ));
      finances.VATIncluded.sold = new Big(
        order.finances &&
        order.finances.VATIncluded &&
        order.finances.VATIncluded.sold &&
        order.finances.VATIncluded.amountStatus == "setByProducer"
          ? order.finances.VATIncluded.sold
          : 0
      );
      finances.VATExcluded.sold = new Big(
        order.finances &&
        order.finances.VATExcluded &&
        order.finances.VATExcluded.sold &&
        order.finances.VATExcluded.amountStatus == "setByProducer"
          ? order.finances.VATExcluded.sold
          : 0
      );

      finances.totals.amountStatus = order.finances && order.finances.totals ? order.finances.totals.amountStatus : null;
      finances.VATIncluded.amountStatus = order.finances && order.finances.totals ? order.finances.totals.amountStatus : null;
      finances.VATExcluded.amountStatus = order.finances && order.finances.totals ? order.finances.totals.amountStatus : null;

      for (const [productKey, value] of Object.entries(order.products.references)) {
        if (productKey !== "deposit" && productKey !== "commission" && productKey !== "deliveryCost") {
          const product: ProductModel = value;
          const calculated = new Big(this.calculateProductTotalAmount(product));
          const { VATExcluded, VATIncluded } = this.calculateProductTotalsAmount(product);
          // console.log(order.key,product.details.name, product.basket, calculated);

          finances.totals.calculated = finances.totals.calculated.plus(calculated);
          finances.VATExcluded.calculated = finances.VATExcluded.calculated.plus(new Big(VATExcluded));
          finances.VATIncluded.calculated = finances.VATIncluded.calculated.plus(new Big(VATIncluded));

          const deposit = this.calculateDeposit(product);
          const depositProduct = this.amountToProduct(deposit, "deposit", product.details.currency);
          const depositVATExcluded = this.calculateProductVATExcludedPrice(depositProduct, countryCode);
          finances.totals.deposit = finances.totals.deposit.plus(new Big(deposit));
          finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(new Big(deposit));
          finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(new Big(depositVATExcluded));

          finances.productCount++;
        }
      }
      if (order.tournee && order.tournee.details && order.tournee.details.commissionValue) {
        const commission = this.calculateCommission(
          finances.VATIncluded.products,
          order.tournee.details.commissionType,
          order.tournee.details.commissionValue
        );
        const commissionVATExcluded = this.calculateCommission(
          finances.VATExcluded.products,
          order.tournee.details.commissionType,
          order.tournee.details.commissionValue
        );
        finances.VATExcluded.commission = finances.VATExcluded.commission.plus(new Big(commissionVATExcluded));
        finances.VATIncluded.commission = finances.VATIncluded.commission.plus(new Big(commission));
        finances.totals.commission = finances.totals.commission.plus(new Big(commission));
      }
      if (order.tournee && order.tournee.details && order.tournee.details.deliveryCost) {
        const deliveryProduct = this.amountToProduct(order.tournee.details.deliveryCost, "delivery", currency);
        const deliveryVATExcluded = this.calculateProductVATExcludedPrice(deliveryProduct, countryCode);
        finances.totals.shippingCost = finances.totals.shippingCost.plus(new Big(order.tournee.details.deliveryCost));
        finances.VATIncluded.shippingCost = finances.VATIncluded.shippingCost.plus(new Big(order.tournee.details.deliveryCost));
        finances.VATExcluded.shippingCost = finances.VATExcluded.shippingCost.plus(new Big(deliveryVATExcluded));
      }
      if (order.tournee && order.tournee.details && order.tournee.details.deposit) {
        const depositProduct = this.amountToProduct(order.tournee.details.deposit, "deposit", currency);
        const depositVATExcluded = this.calculateProductVATExcludedPrice(depositProduct, countryCode);
        finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(new Big(depositVATExcluded));
        finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(new Big(order.tournee.details.deposit));
        finances.totals.deposit = finances.totals.deposit.plus(new Big(order.tournee.details.deposit));
      }
      if (order.payments) {
        for (const [key, value] of Object.entries(order.payments)) {
          const payment: IPayment = value;
          if (payment.status == "paid") {
            const paid = payment.debitedFunds.amount;
            finances.totals.paid = finances.totals.paid.plus(new Big(paid));
            // console.log(value);
          }
        }
      }
      // console.log(finances);
      if (order.finances && order.finances.totals.amountStatus !== "setByProducer") {
        finances.totals.products = finances.totals.calculated;
        finances.VATExcluded.products = finances.VATExcluded.calculated;
        finances.VATIncluded.products = finances.VATIncluded.calculated;
        finances.totals.sold = this.calculateSold(finances.totals);
        finances.VATExcluded.sold =  this.calculateSold(finances.VATExcluded);
        finances.VATIncluded.sold =  this.calculateSold(finances.VATIncluded);
        // console.log('not set by producter',parseFloat(finances.totals.sold.toFixed(2)),parseFloat(finances.totals.calculated.toFixed(2)),parseFloat(finances.totals.products.toFixed(2)))
      } else {
        // if the total amount has been explicitely set by the producer, we estimate the value of the products by substracting the deposit, commission and shipping cost)
        finances.totals.products = finances.totals.sold.minus(
          finances.totals.commission.plus(finances.totals.deposit).plus(finances.totals.shippingCost)
        );
        finances.VATExcluded.products = finances.VATExcluded.sold.minus(
          finances.VATExcluded.commission.plus(finances.VATExcluded.deposit).plus(finances.VATExcluded.shippingCost)
        );
        finances.VATIncluded.products = finances.VATIncluded.sold.minus(
          finances.VATIncluded.commission.plus(finances.VATIncluded.deposit).plus(finances.VATIncluded.shippingCost)
        );
        // console.log('sold',order.finances.totals.sold );
        // console.log('set by producter',parseFloat(finances.totals.sold.toFixed(2)),parseFloat(finances.totals.calculated.toFixed(2)),parseFloat(finances.totals.products.toFixed(2)))
        // console.log('set by producter charges',parseFloat(finances.totals.commission.toFixed(2)),parseFloat(finances.totals.deposit.toFixed(2)),parseFloat(finances.totals.shippingCost.toFixed(2)))
      }
      order.finances = this.financesBigToFloat(finances);
      order.paymentStatus = this.checkPaymentStatus(order.finances);
    }
    return order;
  }

  public calculateMultiTourneesRevenue(ordersOrganized: any[]) {
    let finances = this.getEmptyFinances();
    for (const status of ordersOrganized) {
      // console.log("status", status);
      if (status.name !== "rejected" && status.name !== "cancelled" && status.name !== "notDelivered" && status.finances) {
        finances = this.addFinances(finances, status.finances);
      }
    }
    return this.financesBigToFloat(finances);
  }

  // deprecated
  public updateTourneeRevenue(tournee: TourneeModel, computeRejected = false) {
    console.log("updateTourneeRevenue");
    const finances = {
      orderCount: 0,
      orderRejected: 0,
      productCount: 0,
      totals: { paid: new Big(0), sold: new Big(0), soldWithOutShipping: new Big(0), shippingCost: new Big(0), deposit: new Big(0) },
      subtypes: {}
    };
    if (tournee.orders) {
      for (const [key, value] of Object.entries(tournee.orders)) {
        let order: OrderModel = value;
        order = this.calculateOrderRevenue(order);
        tournee.orders[key] = order;
        finances.totals.sold = finances.totals.sold.plus(
          new Big(["rejected", "cancelled"].includes(order.status) ? 0 : order.finances.totals.sold)
        );
        finances.totals.paid = finances.totals.paid.plus(new Big(order.finances.totals.paid));
        finances.totals.deposit = finances.totals.deposit.plus(new Big(order.finances.totals.deposit));
        finances.totals.shippingCost = finances.totals.shippingCost.plus(new Big(order.finances.totals.shippingCost));
        if (order.status == "rejected" || order.status == "cancelled" || order.status == "notDelivered") {
          finances.orderRejected += 1;
        } else {
          finances.orderCount += 1;
        }
      }
    }
    tournee.finances = this.financesBigToFloat(finances);
    tournee.paymentStatus = this.checkPaymentStatus(tournee.finances);
    return tournee;
  }

  checkPaymentStatus(finances: any): string {
    if (finances.totals.sold == finances.totals.paid) {
      return "cashedIn";
    } else if (finances.totals.sold < finances.totals.paid) {
      return "overPaid";
    } else {
      return "unPaid";
    }
  }

  public roundNumber(value: number, decimal: number) {
    const factor = 10 ^ decimal;
    return Math.round(value * factor + Number.EPSILON) / factor;
    //return Math.round(value*factor)/factor;
  }

  public sum(value1, value2) {
    const value1Big = new Big(value1);
    const value2Big = new Big(value2);
    const result = value1Big.add(value2Big);
    return parseFloat(result.toFixed(2));
  }

  public financesBigToFloat(finances: any) {
    const financesInFloat = {
      orderCount: finances.orderCount || 0,
      orderRejected: finances.orderRejected || 0,
      productCount: finances.productCount || 0,
      totals: this.totalsBigToFloat(finances.totals),
      VATIncluded: finances.VATIncluded ? this.totalsBigToFloat(finances.VATIncluded) : null,
      VATExcluded: finances.VATExcluded ? this.totalsBigToFloat(finances.VATExcluded) : null
    };
    // console.log(finances, financesInFloat);
    return financesInFloat;
  }

  public totalsBigToFloat(totals: any) {
    const totalsInFloat = {
        commission: parseFloat(totals.commission && totals.commission ? totals.commission.toFixed(2) : 0),
        amountStatus: totals?.amountStatus || "unknown",
        shippingCost: parseFloat(
          totals.shippingCost && totals.shippingCost ? totals.shippingCost.toFixed(2) : 0
        ),
        deposit: parseFloat(totals && totals.deposit ? totals.deposit.toFixed(2) : 0),
        paid: parseFloat(totals && totals.paid ? totals.paid.toFixed(2) : 0),
        prepayable: parseFloat(totals && totals.prepayable ? totals.prepayable.toFixed(2) : 0),
        calculated: parseFloat(totals && totals.calculated ? totals.calculated.toFixed(2) : 0),
        sold: parseFloat(totals && totals.sold ? totals.sold.toFixed(2) : 0),
        products: parseFloat(totals && totals.products ? totals.products.toFixed(2) : 0)
    };
    // console.log(finances, financesInFloat);
    return totalsInFloat;
  }

  public financesFloatToBig(finances: any) {
    const financesInBig = {
      orderCount: finances.orderCount || 0,
      orderRejected: finances.orderRejected || 0,
      productCount: finances.productCount || 0,
      totals: {
        commission: new Big(finances.totals.commission || 0),
        amountStatus: finances.totals.amountStatus,
        shippingCost: new Big(finances.totals.shippingCost || 0),
        deposit: new Big(finances.totals.deposit || 0),
        paid: new Big(finances.totals.paid || 0),
        prepayable: new Big(finances.totals.prepayable || 0),
        calculated: new Big(finances.totals.calculated || 0),
        sold: new Big(finances.totals.sold || 0),
        products: new Big(finances.totals.products || 0)
      },
      VATIncluded: {
        commission: new Big(finances.VATIncluded.commission || 0),
        amountStatus: finances.VATIncluded.amountStatus,
        shippingCost: new Big(finances.VATIncluded.shippingCost || 0),
        deposit: new Big(finances.VATIncluded.deposit || 0),
        paid: new Big(finances.VATIncluded.paid || 0),
        prepayable: new Big(finances.VATIncluded.prepayable || 0),
        calculated: new Big(finances.VATIncluded.calculated || 0),
        sold: new Big(finances.VATIncluded.sold || 0),
        products: new Big(finances.VATIncluded.products || 0)
      },
      VATExcluded: {
        commission: new Big(finances.VATExcluded.commission || 0),
        amountStatus: finances.VATExcluded.amountStatus,
        shippingCost: new Big(finances.VATExcluded.shippingCost || 0),
        deposit: new Big(finances.VATExcluded.deposit || 0),
        paid: new Big(finances.VATExcluded.paid || 0),
        prepayable: new Big(finances.VATExcluded.prepayable || 0),
        calculated: new Big(finances.VATExcluded.calculated || 0),
        sold: new Big(finances.VATExcluded.sold || 0),
        products: new Big(finances.VATExcluded.products || 0)
      }
    };
    // console.log(finances, financesInFloat);
    return financesInBig;
  }

  public addFinances(finances, additionalFinances) {
    finances.totals.sold = finances.totals.sold.plus(new Big(additionalFinances.totals.sold || additionalFinances.totals.paid || 0));
    finances.totals.calculated = finances.totals.calculated.plus(new Big(additionalFinances.totals.calculated || 0));
    finances.totals.deposit = finances.totals.deposit.plus(new Big(additionalFinances.totals.deposit || 0));
    finances.totals.paid = finances.totals.paid.plus(new Big(additionalFinances.totals.paid || 0));
    finances.totals.products = finances.totals.products.plus(new Big(additionalFinances.totals.products || 0));
    finances.totals.commission = finances.totals.commission.plus(new Big(additionalFinances.totals.commission || 0));
    finances.totals.shippingCost = finances.totals.shippingCost.plus(new Big(additionalFinances.totals.shippingCost || 0));

    if(finances.VATIncluded && additionalFinances.VATIncluded){
      finances.VATIncluded.sold = finances.VATIncluded.sold.plus(
        new Big(additionalFinances.VATIncluded.sold || additionalFinances.VATIncluded.paid || 0)
      );
      finances.VATIncluded.calculated = finances.VATIncluded.calculated.plus(new Big(additionalFinances.VATIncluded.calculated || 0));
      finances.VATIncluded.deposit = finances.VATIncluded.deposit.plus(new Big(additionalFinances.VATIncluded.deposit || 0));
      finances.VATIncluded.paid = finances.VATIncluded.paid.plus(new Big(additionalFinances.VATIncluded.paid || 0));
      finances.VATIncluded.products = finances.VATIncluded.products.plus(new Big(additionalFinances.VATIncluded.products || 0));
      finances.VATIncluded.commission = finances.VATIncluded.commission.plus(new Big(additionalFinances.VATIncluded.commission || 0));
      finances.VATIncluded.shippingCost = finances.VATIncluded.shippingCost.plus(new Big(additionalFinances.VATIncluded.shippingCost || 0));  
    }
    if(finances.VATExcluded && additionalFinances.VATExcluded){
      finances.VATExcluded.sold = finances.VATExcluded.sold.plus(
        new Big(additionalFinances.VATExcluded.sold || additionalFinances.VATExcluded.paid || 0)
      );
      finances.VATExcluded.calculated = finances.VATExcluded.calculated.plus(new Big(additionalFinances.VATExcluded.calculated || 0));
      finances.VATExcluded.deposit = finances.VATExcluded.deposit.plus(new Big(additionalFinances.VATExcluded.deposit || 0));
      finances.VATExcluded.paid = finances.VATExcluded.paid.plus(new Big(additionalFinances.VATExcluded.paid || 0));
      finances.VATExcluded.products = finances.VATExcluded.products.plus(new Big(additionalFinances.VATExcluded.products || 0));
      finances.VATExcluded.commission = finances.VATExcluded.commission.plus(new Big(additionalFinances.VATExcluded.commission || 0));
      finances.VATExcluded.shippingCost = finances.VATExcluded.shippingCost.plus(new Big(additionalFinances.VATExcluded.shippingCost || 0));
    }

    finances.productCount += additionalFinances.productCount;
    finances.orderCount += additionalFinances.orderCount || 0;
    return finances;
  }

  bigToFloat(big: Big): number {
    return parseFloat(big.toFixed(2));
  }

  calculatePosition(from, to, array) {
    const length = array.length;
    const defaultIncrement = 32768;
    // console.log(from,to,array)
    if (to === 0 && length >= 1) {
      //first element
      return array[to].position / 2;
    } else if (to == array.length - 1) {
      // move to last position
      return (Math.floor(array[to].position / defaultIncrement) + 1) * defaultIncrement;
    } else if (to < from) {
      // move up between two
      return array[to - 1].position + (array[to].position - array[to - 1].position) / 2;
    } else {
      // move down between two
      return array[to].position + (array[to + 1].position - array[to].position) / 2;
    }
  }

  calculatePositionNewElement(arrayPosition, array) {
    const length = array.length;
    const defaultIncrement = 32768;
    if (arrayPosition === 0 && length === 0) {
      // new element in empty list
      return defaultIncrement;
    } else if (arrayPosition === array.length) {
      // new element at the end
      return (Math.floor(array[arrayPosition - 1].position / defaultIncrement) + 1) * defaultIncrement;
    }
  }

  convertAmountToSmallestCurrencyDecimal(amount: number, currency: string | "EUR") {
    if (typeof currency == "undefined" || currency == null) {
      currency = "EUR";
    }
    return amount * Math.pow(10, currencies[currency.toUpperCase()].decimal_digits);
  }

  convertAmountToIntCurrency(amount: number, currency: string) {
    return amount / Math.pow(10, currencies[currency.toUpperCase()].decimal_digits);
  }

  sliceText(text: string, length: number): string {
    if (text) {
      return text.length < length ? text : text.slice(0, length - 3) + "...";
    } else {
      return "";
    }
  }

  public getCurrencyMask(currency: string, min?: number) {
    const mask: any = {
      decimalZeroPadding: true,
      precision: currencies[currency.toUpperCase()].decimal_digits,
      decimalSeparator: currencies[currency.toUpperCase()].decimal_separator,
      min: min
    };
    if (currencies[currency.toUpperCase()].code_position == "prefix") {
      mask.prefix = currencies[currency.toUpperCase()].code + " ";
    } else {
      mask.postfix = " " + currencies[currency.toUpperCase()].code;
    }

    return maskitoNumberOptionsGenerator(mask);
  }

  public getPercentageMask(min?: number) {
    const mask: any = {
      decimalZeroPadding: true,
      precision: 1,
      min: min
    };
    mask.postfix = " %";

    return maskitoNumberOptionsGenerator(mask);
  }

  public stringToNumber(input: string | number) {
    return Number(typeof input === "string" ? input.replace(/[a-zA-Z ]/g, "").replace(",", ".") : input);
  }

  calculateVATExcludedPrice(priceVATIncluded: number, VATRate: number, currency: string) {
    const VATRateBig = new Big(VATRate).div(new Big(100));
    const decimals = currencies[currency.toUpperCase() || "EUR"].decimal_digits || 2;
    const priceBig = new Big(priceVATIncluded);
    const exclVATPriceBig = priceBig.div(VATRateBig.plus(new Big(1))).round(decimals);
    return parseFloat(exclVATPriceBig.toFixed(2));
  }

  calculateCommission(amount: number, commissionType: string, commissionValue: number) {
    if (commissionType == "percentage") {
      return parseFloat(((Big(amount).times(Big(commissionValue))).div(Big(100))).toFixed(2));
    } else {
      return commissionValue;
    }
  }
}
